<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2023-06-19 17:57:32
 * @LastEditors: luckymiaow
-->
<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2022-05-24 17:43:51
 * @LastEditors: 景 彡
-->

<script setup lang="ts">
import { computed, ref, useAttrs, useSlots, watchEffect } from 'vue';
import { LoadingOutlined } from '@ant-design/icons-vue';
import type { IStandardSelectProps, StandardItem, StandardModel } from '../types';
import useStandard from './useStandard';
import Select from './StandardSelect.vue';
import { Cascader } from '../../cascader';
import { RadioGroup } from '../../radio';
import { TreeSelect } from '../../tree-select';

const props = defineProps<{
  allApi?(): Promise<StandardModel[]>;
  byKeyApi?({ key }: { key: string }): Promise<StandardModel>;
  sKey: string; // 标准key
  el?: 'select' | 'cascader' | 'radio' | 'tree-select';
}>();

const emit = defineEmits(['update:value']);

const attrs = useAttrs();

const { getBindValue, standard, standardChange, loading } = useStandard<IStandardSelectProps>({ attrs, props });

const modelValue = ref();

function update(...ags: any[]) {
  emit('update:value', ...ags);
}

watchEffect(() => {
  if (!props.el && !useSlots().el) {
    console.error('el is required, value as a string | (() => VNode | JSX.Element) | Component or slot');
  }
  modelValue.value = getBindValue.value.value;
});

/**
 * 获取属性
 */
const field = computed(() => {
  const temp = getBindValue.value as { [key: string]: any };

  if (props.el === 'select') {
    if (!('showArrow' in temp)) temp!.showArrow = true;
  }

  if (props.el && ['select', 'cascader', 'radio'].includes(props.el)) {
    temp!.options = standard.value?.standardItems ?? [];
  }

  if (props.el === 'tree-select') {
    temp!.treeData = standard.value?.standardItems ?? [];
  }

  return temp;
});

function getComponent(el?: 'select' | 'cascader' | 'radio' | 'tree-select') {
  if (!el) return console.error('Invalid el, el is  select | cascader | radio | tree-select | radio');

  if (loading.value === true && ['radio'].includes(el)) {
    return LoadingOutlined;
  }
  const baseComponents: Record<NonNullable<typeof el>, any> = {
    select: Select,
    cascader: Cascader,
    radio: RadioGroup,
    'tree-select': TreeSelect,
  };
  if (el in baseComponents) return baseComponents[el];
}

function findDefaultItems(items: StandardItem[]): StandardItem[] {
  const result: StandardItem[] = [];

  for (const item of items) {
    if (item.isDefault) {
      result.push(item);
    }

    if (item.children && item.children.length > 0) {
      result.push(...findDefaultItems(item.children));
    }
  }

  return result;
}

function defaultValue() {
  if (props.el === 'tree-select') {
    let result;
    const data: StandardItem[] = findDefaultItems(field.value.treeData ?? []);
    if ('multiple' in field.value || field.value.multiple) {
      if (getBindValue.value.labelInValue) {
        result = data.map(e => {
          return { value: e.value, label: e.label };
        });
      } else result = data.map(e => e.value);
    } else if (getBindValue.value.labelInValue) {
      result = { label: data[0].label, value: data[0].value };
    } else {
      result = data[0].value;
    }
    return result;
  }
  return modelValue.value;
}

standardChange(() => {
  modelValue.value = getBindValue.value.value || defaultValue();
  update(modelValue.value);
});
</script>

<template>
  <template v-if="'el' in $slots">
    <slot name="el" v-bind="{ standard, standardChange, loading, options: standard?.standardItems ?? [] }"></slot>
  </template>

  <component :is="getComponent(el)" v-else :value="modelValue" v-bind="field" @update:value="update">
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <template v-if="loading" #suffixIcon> <LoadingOutlined spin /> </template>
  </component>
</template>
